import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useFitpass } from "@/hooks/useFitpass";
import { employeesAPI } from "@/services/api";
import { ROLES } from "@/constants";
import { Employee } from "@/types";
import {
  monthLabelToParts,
  monthLabelToUTCISO,
  formatFitpassMonth,
  getClientTimezoneOffset,
  getNextMonthLabel,
  getCurrentMonthLabel,
  getNextMonthLabelFrom,
  getCreateMonthOptions,
  getFilterMonthOptions,
} from "@/utils/fitpassMonth";
import { AdditionalPerson, FitpassRequest } from "@/types";

export const useFitpassPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;
  const {
    allRequests,
    isLoading,
    createRequest,
    updateRequest,
    updatePaid,
    updateAdditionalPersonPaid,
    deleteRequest,
    copyToNextMonth,
    isCopying,
    isUpdating,
  } = useFitpass();

  const { data: employees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: ["fitpass", "employees"],
    queryFn: employeesAPI.getActive,
    enabled: isAdmin,
  });

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [createMonth, setCreateMonth] = useState("");
  const [additionalPeople, setAdditionalPeople] = useState<AdditionalPerson[]>(
    [],
  );
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<FitpassRequest | null>(
    null,
  );
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [requestToEdit, setRequestToEdit] = useState<FitpassRequest | null>(
    null,
  );
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [editAdditionalPeople, setEditAdditionalPeople] = useState<
    AdditionalPerson[]
  >([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthLabel);

  useEffect(() => {
    if (isAdmin && employees.length > 0) {
      setSelectedEmployee(employees[0]);
      setCreateMonth((prev) => prev || getNextMonthLabel());
    }
  }, [employees, isAdmin]);

  const createMonthOptions = useMemo(() => getCreateMonthOptions(), []);
  const filterMonthOptions = useMemo(() => getFilterMonthOptions(), []);

  const filteredRequests = useMemo(() => {
    if (!isAdmin) {
      return allRequests as FitpassRequest[];
    }
    return (allRequests as FitpassRequest[]).filter(
      (request) => formatFitpassMonth(request.month) === selectedMonth,
    );
  }, [allRequests, selectedMonth, isAdmin]);

  const isViewingCurrentMonth = selectedMonth === getCurrentMonthLabel();
  const nextMonthLabel = getNextMonthLabelFrom(selectedMonth);

  const canEditRequestPayment = useCallback(
    (employeeId: string) => isAdmin || employeeId === user?.employee?.id,
    [isAdmin, user?.employee?.id],
  );

  const updateAdditionalPersonField = (
    people: AdditionalPerson[],
    index: number,
    field: "name" | "surname" | "email",
    value: string,
  ) => {
    const updated = [...people];
    updated[index] = { ...updated[index], [field]: value };
    return updated;
  };

  const handleAddPerson = () => {
    setAdditionalPeople([
      ...additionalPeople,
      { name: "", surname: "", email: "" },
    ]);
  };

  const handleRemovePerson = (index: number) => {
    setAdditionalPeople(additionalPeople.filter((_, i) => i !== index));
  };

  const handlePersonChange = (
    index: number,
    field: "name" | "surname" | "email",
    value: string,
  ) => {
    setAdditionalPeople(
      updateAdditionalPersonField(additionalPeople, index, field, value),
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!isAdmin) {
      setError("Only admins can create Fitpass memberships.");
      return;
    }

    if (!selectedEmployee) {
      setError("Select an employee to add to Fitpass.");
      return;
    }

    try {
      await createRequest({
        employeeId: selectedEmployee.id,
        employeeName: selectedEmployee.firstName,
        employeeSurname: selectedEmployee.lastName,
        employeeEmail: selectedEmployee.user?.email || "",
        additionalPeople:
          additionalPeople.length > 0 ? additionalPeople : undefined,
        month: monthLabelToUTCISO(createMonth || getNextMonthLabel()),
      });
      setSuccess("Fitpass membership added for the selected employee.");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to submit request");
    }
  };

  const handleEditClick = (request: FitpassRequest) => {
    const employee =
      employees.find((entry) => entry.id === request.employeeId) ||
      ({
        id: request.employeeId,
        firstName: request.employeeName,
        lastName: request.employeeSurname,
        user: { email: request.employeeEmail },
      } as Employee);

    setRequestToEdit(request);
    setEditEmployee(employee);
    setEditAdditionalPeople(
      (request.additionalPeople || []).map((person) => ({
        name: person.name,
        surname: person.surname,
        email: person.email,
        paid: person.paid || false,
      })),
    );
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setRequestToEdit(null);
    setEditEmployee(null);
    setEditAdditionalPeople([]);
  };

  const handleEditAddPerson = () => {
    setEditAdditionalPeople([
      ...editAdditionalPeople,
      { name: "", surname: "", email: "", paid: false },
    ]);
  };

  const handleEditRemovePerson = (index: number) => {
    setEditAdditionalPeople(editAdditionalPeople.filter((_, i) => i !== index));
  };

  const handleEditPersonChange = (
    index: number,
    field: "name" | "surname" | "email",
    value: string,
  ) => {
    setEditAdditionalPeople(
      updateAdditionalPersonField(editAdditionalPeople, index, field, value),
    );
  };

  const handleEditSubmit = async () => {
    if (!requestToEdit || !editEmployee) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      await updateRequest({
        id: requestToEdit.id,
        data: {
          employeeId: editEmployee.id,
          employeeName: editEmployee.firstName,
          employeeSurname: editEmployee.lastName,
          employeeEmail:
            editEmployee.user?.email || requestToEdit.employeeEmail,
          additionalPeople:
            editAdditionalPeople.length > 0 ? editAdditionalPeople : undefined,
          month: requestToEdit.month,
        },
      });
      setSuccess("Fitpass request updated successfully.");
      handleEditClose();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update request");
    }
  };

  const handleDeleteClick = (request: FitpassRequest) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) {
      return;
    }

    try {
      await deleteRequest(requestToDelete.id);
      setSuccess("Fitpass membership removed.");
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete request");
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRequestToDelete(null);
  };

  const handlePaidToggle = async (
    requestId: string,
    currentPaidStatus: boolean,
  ) => {
    try {
      await updatePaid({ id: requestId, paid: !currentPaidStatus });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update paid status");
    }
  };

  const handleAdditionalPersonPaidToggle = async (
    requestId: string,
    personIndex: number,
    currentPaidStatus: boolean,
  ) => {
    try {
      await updateAdditionalPersonPaid({
        id: requestId,
        personIndex,
        paid: !currentPaidStatus,
      });
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to update additional person paid status",
      );
    }
  };

  const handleCopyToNextMonth = async () => {
    setError("");
    setSuccess("");

    try {
      const result = await copyToNextMonth({
        ...monthLabelToParts(selectedMonth),
        timezoneOffset: getClientTimezoneOffset(),
      });

      const skippedMessage =
        result.skipped > 0
          ? ` ${result.skipped} skipped because they already exist in ${nextMonthLabel}.`
          : "";

      setSuccess(
        `Copied ${result.copied} request${result.copied === 1 ? "" : "s"} to ${nextMonthLabel}. All paid statuses were reset.${skippedMessage}`,
      );
      setSelectedMonth(nextMonthLabel);
      setCopyDialogOpen(false);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to copy requests");
      setCopyDialogOpen(false);
    }
  };

  return {
    isAdmin,
    isLoading,
    isLoadingEmployees,
    isCopying,
    isUpdating,
    employees,
    success,
    error,
    selectedEmployee,
    setSelectedEmployee,
    createMonth,
    setCreateMonth,
    defaultMonthLabel: getNextMonthLabel(),
    createMonthOptions,
    additionalPeople,
    filteredRequests,
    selectedMonth,
    setSelectedMonth,
    filterMonthOptions,
    isViewingCurrentMonth,
    nextMonthLabel,
    copyDialogOpen,
    setCopyDialogOpen,
    editDialogOpen,
    editEmployee,
    setEditEmployee,
    editAdditionalPeople,
    deleteDialogOpen,
    requestToDelete,
    canEditRequestPayment,
    handleAddPerson,
    handleRemovePerson,
    handlePersonChange,
    handleSubmit,
    handleEditClick,
    handleEditClose,
    handleEditAddPerson,
    handleEditRemovePerson,
    handleEditPersonChange,
    handleEditSubmit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handlePaidToggle,
    handleAdditionalPersonPaidToggle,
    handleCopyToNextMonth,
  };
};
